import React, {
  useState,
  createContext,
  useRef,
  useEffect,
  useMemo,
  useContext,
} from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import "react-datepicker/dist/react-datepicker.css";
import "./Estimate.css";
import WorkTab from "./WorkTab/WorkTab";
import ProjectTab from "./ProjectTab/ProjectTab";
import LeadTab from "./LeadTab/LeadTab";
import QualityTab from "./QualityTab/QualityTab";
import BimTab from "./BimTab/BimTab";
import ContractSectionsTab from "./ContractSectionsTab/ContractSectionsTab";
import VersionHistoryDrawer from "./VersionHistoryDrawer/VersionHistoryDrawer";
import ApprovalModal from "./ApprovalModal/ApprovalModal";
import { EstimateStateProvider, useEstimateState } from "./EstimateContext";
import Preloader from "../../../Preloader/Preloader";
import {
  requestProjectEstimateApproval,
  getCpTeis,
  restoreVersion,
  getProjectEstimateApprovals,
  updateThresholdCostPercent,
} from "../../../../api/projectEstimatesApi";
import { getProjectById } from "../../../../api/projectApi";
import {
  getProjectApprovalStatuses,
  cancelApprovalRequest,
} from "../../../../api/projectApprovalApi";
import { UserContext } from "../../../../contexts/CurrentUserContext";

// Импорты иконок
import Coinssvg from "../../../../images/projects/ProjectFinance/Coinssvg.svg";
import Calc from "../../../../images/projects/ProjectFinance/Calc.svg";
import House from "../../../../images/projects/ProjectFinance/House.svg";
import Sigma from "../../../../images/projects/ProjectFinance/Sigma.svg";

registerLocale("ru", ru);

// Контекст для передачи данных между компонентами
export const EstimateContext = createContext();

const bottomTabs = [
  { id: "work", label: "Рабочее проектирование" },
  { id: "bim", label: "BIM координирование" },
  { id: "quality", label: "Контроль качества" },
  { id: "lead", label: "Ведение проекта" },
  { id: "project", label: "Управление проектом" },
  { id: "contract", label: "Разделы на договоре" },
];

// удалён: TableCell (неиспользуется)

// удалён: ProfitCircle (неиспользуется)

/**
 * Компонент сметы
 *
 * ОБНОВЛЕНО:
 * - Использует новые функции API через контекст (loadEstimateData)
 * - Упрощена логика загрузки данных
 * - Удалены дублирующие вызовы API
 * - Восстановлен блок прибыли справа с данными из API
 *
 * СООТВЕТСТВИЕ ПОЛЕЙ API:
 * - contract_value: Сумма основного договора
 * - contract_value_before_nds: Сумма за вычетом НДС
 * - contract_value_construction: На разработку
 * - contract_value_construction_percent: процент На разработку
 * - cost_price: Себестоимость-план (также используется как Доход с хардкодом 100%)
 * - difference: Отклонения
 * - nds_value: сумма НДС
 * - indirect_expenditures: Косвенные расходы
 * - indirect_expenditures_percent: Процент косвенных расходов
 * - cost_price_percent: процент Себестоимость-план
 * - pure_income: Чистая прибыль
 * - pure_income_percent: процент Чистая прибыль
 */
const EstimateComponent = ({ projectId, onSave, isReadOnly = false, projectLockedByApproval = false }) => {
  // Получаем функцию сохранения из контекста
  const { performSave } = useEstimateState();
  const { userData } = useContext(UserContext);

  const [activeBottomTab, setActiveBottomTab] = useState(() => {
    // Проверяем, есть ли сохраненное состояние для этого проекта
    const savedBottomTab = localStorage.getItem(
      `estimate_activeBottomTab_${projectId}`
    );

    // Если есть сохраненное состояние - используем его
    // Если нет - показываем первую вкладку (первый вход на проект)
    return savedBottomTab || "work";
  });
  // удалено: expandedRows
  const [showVersionDrawer, setShowVersionDrawer] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const workTabRef = useRef();
  const bimTabRef = useRef();
  const qualityTabRef = useRef();
  const projectTabRef = useRef();
  const leadTabRef = useRef();
  const contractSectionsTabRef = useRef();

  // Состояния для данных шапки
  const [headerData, setHeaderData] = useState({
    contract_value: 0,
    contract_value_before_nds: 0,
    contract_value_construction: 0,
    contract_value_construction_percent: 0,
    cost_price: 0,
    difference: 0,
    // Добавляем все остальные поля из API
    nds_value: 0,
    nds_percent: 0,
    total_sum: 0,
    total_sum_percent: 0,
    indirect_expenditures: 0,
    indirect_expenditures_percent: 0,
    cost_price_percent: 0,
    pure_income: 0,
    pure_income_percent: 0,
    total_construction_design: {},
    bonus_percent_construction_design: 0,
    bim_coordination: {},
    quality_control: {},
    project_delivery: {},
    project_management: {},
    total_with_nds_from_cp: 0,
  });

  // Состояния для редактируемых инпутов
  // удалены: демо-поля avgRate/bonusPercent/laborCosts

  // Состояния для полей заявки
  const [contractNumber, setContractNumber] = useState("001");
  const [contractDate, setContractDate] = useState(new Date("2025-01-01"));

  // Состояния для согласования сметы
  const [projectData, setProjectData] = useState(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [approvalStatuses, setApprovalStatuses] = useState([]);
  const [isCancellingApproval, setIsCancellingApproval] = useState(false);

  // Статусы согласований из ТЭП
  const [cpApprovalStatus, setCpApprovalStatus] = useState(null);
  const [peApprovalStatus, setPeApprovalStatus] = useState(null);
  const [isCreatingCopy, setIsCreatingCopy] = useState(false);
  const [estimateApprovals, setEstimateApprovals] = useState([]); // Список согласований сметы с версиями

  // Флаг редактируемости сметы
  const [isEditable, setIsEditable] = useState(true);

  // Для второй вкладки:
  // удалены: managePercent/manageBonusPercent/manageLabor

  // Получаем функции из нового контекста состояния
  const {
    updateEstimateSection,
    loadEstimateData,
    estimateData,
    isLoading: contextLoading,
    error: contextError,
  } = useEstimateState();

  const [developmentPercentApplied, setDevelopmentPercentApplied] = useState("0");
  const [developmentPercentDraft, setDevelopmentPercentDraft] = useState("0");
  const [isDevelopmentPercentEditing, setIsDevelopmentPercentEditing] =
    useState(false);
  const [isDevelopmentPercentSubmitting, setIsDevelopmentPercentSubmitting] =
    useState(false);

  const parsePercentValue = (rawValue) => {
    const parsed = Number.parseFloat(String(rawValue).replace(",", "."));
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(parsed, 0);
  };

  const formatPercentDisplay = (value) => {
    if (!Number.isFinite(value)) return "0";
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(2).replace(/\.?0+$/, "");
  };

  // Функция для обновления данных шапки
  const updateHeaderData = (data) => {
    setHeaderData((prev) => {
      const newData = {
        ...prev,
        // Не обновляем статичные поля договора
        contract_value: prev.contract_value,
        contract_value_before_nds: prev.contract_value_before_nds,
        contract_value_construction:
          data.contract_value_construction !== undefined
            ? data.contract_value_construction
            : prev.contract_value_construction,
        // Обновляем только динамические поля
        contract_value_construction_percent:
          data.contract_value_construction_percent !== undefined
            ? data.contract_value_construction_percent
            : prev.contract_value_construction_percent,
        cost_price:
          data.cost_price !== undefined ? data.cost_price : prev.cost_price,
        difference:
          data.difference !== undefined ? data.difference : prev.difference,
        // Обновляем все остальные поля из API
        nds_value:
          data.nds_value !== undefined ? data.nds_value : prev.nds_value,
        nds_percent:
          data.nds_percent !== undefined ? data.nds_percent : prev.nds_percent,
        total_sum:
          data.total_sum !== undefined ? data.total_sum : prev.total_sum,
        total_sum_percent:
          data.total_sum_percent !== undefined
            ? data.total_sum_percent
            : prev.total_sum_percent,
        indirect_expenditures:
          data.indirect_expenditures !== undefined
            ? data.indirect_expenditures
            : prev.indirect_expenditures,
        indirect_expenditures_percent:
          data.indirect_expenditures_percent !== undefined
            ? data.indirect_expenditures_percent
            : prev.indirect_expenditures_percent,
        cost_price_percent:
          data.cost_price_percent !== undefined
            ? data.cost_price_percent
            : prev.cost_price_percent,
        pure_income:
          data.pure_income !== undefined ? data.pure_income : prev.pure_income,
        pure_income_percent:
          data.pure_income_percent !== undefined
            ? data.pure_income_percent
            : prev.pure_income_percent,
        total_construction_design:
          data.total_construction_design !== undefined
            ? data.total_construction_design
            : prev.total_construction_design,
        bonus_percent_construction_design:
          data.bonus_percent_construction_design !== undefined
            ? data.bonus_percent_construction_design
            : prev.bonus_percent_construction_design,
        total_with_nds_from_cp:
          data.total_with_nds_from_cp !== undefined
            ? data.total_with_nds_from_cp
            : prev.total_with_nds_from_cp,
        // Фактическая сумма долей из таблицы (Рабочее проектирование) — для проверки «> 100%» и кнопки Сохранить
        percentTotalFromTable:
          data.percentTotalFromTable !== undefined
            ? data.percentTotalFromTable
            : prev.percentTotalFromTable,
      };

      return newData;
    });

    // Обновляем данные вкладок через новый контекст состояния
    if (data.bim_coordination) {
      updateEstimateSection("bim_coordination", data.bim_coordination);
    }
    if (data.quality_control) {
      updateEstimateSection("quality_control", data.quality_control);
    }
    if (data.project_delivery) {
      updateEstimateSection("project_delivery", data.project_delivery);
    }
    if (data.project_management) {
      updateEstimateSection("project_management", data.project_management);
    }
  };

  // Загрузка данных при изменении projectId
  useEffect(() => {
    if (!projectId) return;

    // Используем новую функцию загрузки из контекста
    loadEstimateData(projectId);
  }, [projectId, loadEstimateData]);

  // Загрузка данных проекта для согласования
  useEffect(() => {
    const loadProjectData = async () => {
      if (!projectId) return;

      try {
        const data = await getProjectById(projectId);
        setProjectData(data);
      } catch (error) {
        console.error("Ошибка при загрузке данных проекта:", error);
      }
    };

    loadProjectData();
  }, [projectId]);

  // Загрузка статусов согласования
  useEffect(() => {
    const loadApprovalStatuses = async () => {
      if (!projectId) return;

      try {
        const statuses = await getProjectApprovalStatuses(parseInt(projectId));
        setApprovalStatuses(statuses);
      } catch (error) {
        console.error("Ошибка при загрузке статусов согласования:", error);
        // Не критично, продолжаем работу
      }
    };

    loadApprovalStatuses();
  }, [projectId]);

  // Загрузка статусов согласований из ТЭП
  useEffect(() => {
    const loadTeisApprovalStatuses = async () => {
      if (!projectId) return;

      try {
        const teisData = await getCpTeis(projectId);
        setCpApprovalStatus(teisData.cp_approval_status || null);
        setPeApprovalStatus(teisData.pe_approval_status || null);
        // Не устанавливаем isEditable здесь - это делается в другом эффекте
        // который учитывает и estimateData.editable, и статусы согласований
      } catch (error) {
        console.error("Ошибка при загрузке статусов согласований ТЭП:", error);
        // Не критично, продолжаем работу
      }
    };

    loadTeisApprovalStatuses();
  }, [projectId]);

  // Загрузка списка согласований сметы для сравнения версий
  useEffect(() => {
    const loadEstimateApprovals = async () => {
      if (!projectId) return;

      try {
        const approvals = await getProjectEstimateApprovals(projectId);
        setEstimateApprovals(approvals || []);
      } catch (error) {
        console.error("Ошибка при загрузке списка согласований сметы:", error);
        // Не критично, продолжаем работу
        setEstimateApprovals([]);
      }
    };

    loadEstimateApprovals();
  }, [projectId]);

  // Обновление данных шапки при изменении данных в контексте (только при первой загрузке)
  useEffect(() => {
    if (!estimateData) return;

    // Устанавливаем флаг редактируемости из API или из пропа isReadOnly
    // Учитываем статусы согласований: если есть активное согласование (не отмененное),
    // то блокируем редактирование, иначе используем значение из API
    if (isReadOnly) {
      setIsEditable(false);
    } else {
      // Получаем текущую версию из данных сметы
      const currentVersion = estimateData?.version;

      // Получаем максимальную версию из списка согласований сметы
      const maxApprovalVersion =
        estimateApprovals && estimateApprovals.length > 0
          ? Math.max(
              ...estimateApprovals
                .map((a) => a.version)
                .filter((v) => v !== null && v !== undefined)
            )
          : null;

      // Сравниваем версии: если текущая версия больше максимальной из списка согласований,
      // то можно не блокировать редактирование
      const isVersionNewer =
        currentVersion !== null &&
        currentVersion !== undefined &&
        maxApprovalVersion !== null &&
        currentVersion > maxApprovalVersion;

      // Если API явно разрешает редактирование (editable === true), используем это значение
      // Это имеет приоритет над проверкой статусов
      if (estimateData.editable === true) {
        setIsEditable(true);
      } else {
        // Проверяем статусы согласований из ТЭП только если они загружены
        const cpStatus = cpApprovalStatus
          ? (cpApprovalStatus || "").toLowerCase()
          : "";
        const peStatus = peApprovalStatus
          ? (peApprovalStatus || "").toLowerCase()
          : "";

        const isApproved =
          cpStatus === "approved" ||
          cpStatus === "согласован" ||
          peStatus === "approved" ||
          peStatus === "согласован";

        const isCancelled =
          cpStatus === "отменен инициатором" ||
          peStatus === "отменен инициатором";

        // Если есть согласование и оно не отменено, блокируем редактирование,
        // но не блокируем если версия новее максимальной из списка согласований
        if (isApproved && !isCancelled && !isVersionNewer) {
          setIsEditable(false);
        } else {
          // Используем значение из API, если оно явно указано
          // Если версия новее, разрешаем редактирование
          setIsEditable(estimateData.editable !== false || isVersionNewer);
        }
      }
    }

    // Обновляем шапку при каждом обновлении estimateData,
    // чтобы после точечных API (например threshold-cost-percent) UI сразу показывал новые суммы.
    setHeaderData((prev) => ({
      ...prev,
      contract_value: estimateData.contract_value,
      contract_value_before_nds: estimateData.contract_value_before_nds,
      contract_value_construction: estimateData.contract_value_construction,
      contract_value_construction_percent:
        estimateData.contract_value_construction_percent,
      cost_price: estimateData.cost_price,
      difference: estimateData.difference,
      nds_value: estimateData.nds_value,
      nds_percent: estimateData.nds_percent,
      total_sum: estimateData.total_sum,
      total_sum_percent: estimateData.total_sum_percent,
      indirect_expenditures: estimateData.indirect_expenditures,
      indirect_expenditures_percent: estimateData.indirect_expenditures_percent,
      cost_price_percent: estimateData.cost_price_percent,
      pure_income: estimateData.pure_income,
      pure_income_percent: estimateData.pure_income_percent,
      total_construction_design: estimateData.total_construction_design || {},
      bonus_percent_construction_design:
        estimateData.bonus_percent_construction_design,
      total_with_nds_from_cp: estimateData.total_with_nds_from_cp || 0,
    }));
  }, [
    estimateData,
    isReadOnly,
    cpApprovalStatus,
    peApprovalStatus,
    estimateApprovals,
  ]);

  useEffect(() => {
    const percentValue = Number(headerData.contract_value_construction_percent);
    if (!Number.isFinite(percentValue)) {
      setDevelopmentPercentApplied("0");
      setDevelopmentPercentDraft("0");
      return;
    }
    const formatted = formatPercentDisplay(percentValue);
    setDevelopmentPercentApplied(formatted);
    setDevelopmentPercentDraft(formatted);
  }, [headerData.contract_value_construction_percent]);

  // Обновление состояния загрузки и ошибок из контекста
  useEffect(() => {
    setLoading(contextLoading);
    setError(contextError);
  }, [contextLoading, contextError]);

  // Сохраняем состояние нижней вкладки при изменении projectId
  useEffect(() => {
    if (projectId) {
      // Сохраняем состояние только если пользователь активно переключил вкладку
      // (не при первом входе на проект)
      if (
        activeBottomTab !== "work" ||
        localStorage.getItem(`estimate_activeBottomTab_${projectId}`)
      ) {
        localStorage.setItem(
          `estimate_activeBottomTab_${projectId}`,
          activeBottomTab
        );
      }
    }
  }, [activeBottomTab, projectId]);

  // удалены manageTable, manageColumns, handleExpandRow

  // Функция для сохранения данных
  const handleSave = async () => {
    try {
      setSaving(true);

      // Сначала вызываем удаление разделов из WorkTab, если есть удаленные разделы
      if (workTabRef.current && workTabRef.current.deleteSectionsBeforeSave) {
        await workTabRef.current.deleteSectionsBeforeSave();
      }

      // Синхронизируем локальные данные WorkTab с контекстом перед сохранением
      if (workTabRef.current && workTabRef.current.syncDataBeforeSave) {
        workTabRef.current.syncDataBeforeSave();
        // Даем время на синхронизацию состояния
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Синхронизируем локальные данные ContractSectionsTab с контекстом перед сохранением
      if (
        contractSectionsTabRef.current &&
        contractSectionsTabRef.current.syncDataBeforeSave
      ) {
        contractSectionsTabRef.current.syncDataBeforeSave();
        // Даем время на синхронизацию состояния
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // Используем централизованную функцию сохранения из контекста
      // Она сохраняет данные со всех вкладок одновременно
      await performSave(projectId);

      // Показываем уведомление об успешном сохранении
      setSuccessMessage("Данные успешно сохранены!");
      setShowSuccessModal(true);

      // Даем время попапу показаться перед сбросом состояния
      setTimeout(() => {
        setSaving(false);
      }, 100);
    } catch (error) {
      setErrorMessage("Ошибка при сохранении данных: " + error.message);
      setShowErrorModal(true);
      setSaving(false);
    }
  };

  // Обработчик отправки сметы на согласование
  const handleApprovalSubmit = async (selectedApprovers) => {
    if (!projectId || !headerData) {
      alert("Ошибка: отсутствуют данные проекта или сметы");
      return;
    }

    setIsSubmittingApproval(true);
    try {
      const totalPrice = headerData.contract_value || 0;

      console.log("Отправка сметы на согласование:", {
        projectId,
        approverIds: selectedApprovers,
        totalPrice,
      });

      const result = await requestProjectEstimateApproval(
        projectId,
        selectedApprovers,
        totalPrice
      );

      console.log("Результат отправки на согласование:", result);

      // Показываем успешное сообщение
      setSuccessMessage("Смета успешно отправлена на согласование!");
      setShowSuccessModal(true);

      // Закрываем модальное окно
      setShowApprovalModal(false);

      // Обновляем статусы согласования
      const statuses = await getProjectApprovalStatuses(parseInt(projectId));
      setApprovalStatuses(statuses);

      // Перезагружаем данные сметы, чтобы обновился флаг editable
      await loadEstimateData(projectId);

      // Перезагружаем страницу через небольшую задержку, чтобы обновились все данные
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Ошибка при отправке на согласование:", error);
      setErrorMessage(`Ошибка при отправке на согласование: ${error.message}`);
      setShowErrorModal(true);
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  // Обработчик отмены согласования
  const handleCancelApproval = async () => {
    if (!projectId) return;

    if (
      !window.confirm(
        "Вы уверены, что хотите отозвать отправку на согласование?"
      )
    ) {
      return;
    }

    try {
      setIsCancellingApproval(true);
      await cancelApprovalRequest(parseInt(projectId));

      // Обновляем статусы согласования
      const statuses = await getProjectApprovalStatuses(parseInt(projectId));
      setApprovalStatuses(statuses);

      // Перезагружаем данные сметы, чтобы обновился флаг editable
      await loadEstimateData(projectId);

      // Показываем успешное сообщение
      setSuccessMessage("Отправка на согласование успешно отозвана!");
      setShowSuccessModal(true);

      // Перезагружаем страницу через небольшую задержку, чтобы пользователь увидел сообщение
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Ошибка при отмене согласования:", error);
      setErrorMessage(`Ошибка при отмене согласования: ${error.message}`);
      setShowErrorModal(true);
    } finally {
      setIsCancellingApproval(false);
    }
  };

  const getStatusIcon = (status) => {
    if (!status)
      return <span className="estimate-approval-status-icon pending">⏳</span>;
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return <span className="estimate-approval-status-icon approved">✓</span>;
    }
    if (statusLower === "на рассмотрении" || statusLower === "pending") {
      return <span className="estimate-approval-status-icon pending">⏳</span>;
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return <span className="estimate-approval-status-icon rejected">✕</span>;
    }
    return <span className="estimate-approval-status-icon pending">⏳</span>;
  };

  const getStatusText = (status) => {
    if (!status) return "На рассмотрении";
    const statusLower = status.toLowerCase();
    if (statusLower === "согласован" || statusLower === "approved") {
      return "Согласован";
    }
    if (
      statusLower === "на рассмотрении" ||
      statusLower === "pending" ||
      statusLower === "на рассмотрении"
    ) {
      return "На рассмотрении";
    }
    if (statusLower === "не согласован" || statusLower === "rejected") {
      return "Отклонен";
    }
    return "На рассмотрении";
  };

  // Находим активное согласование (только то, которое еще не завершено)
  // Ищем согласование, которое еще не завершено (есть шаги со статусом "pending" или "на рассмотрении")
  // Также проверяем общий статус согласования
  const activeApproval = approvalStatuses.find((approval) => {
    // Проверяем общий статус согласования - исключаем завершенные и отмененные
    const status = approval.status?.toLowerCase() || "";
    if (
      status === "согласован" ||
      status === "отклонен" ||
      status === "отменен инициатором" ||
      status === "не согласован"
    ) {
      return false; // Согласование уже завершено или отменено
    }

    // Если статус "на рассмотрении" - это активное согласование
    if (status === "на рассмотрении") {
      return true;
    }

    // Если есть шаги, проверяем их статусы
    if (approval.steps && approval.steps.length > 0) {
      // Проверяем, есть ли хотя бы один шаг, который еще не завершен
      return approval.steps.some((step) => {
        const stepStatus = step.status?.toLowerCase() || "";
        return (
          stepStatus === "pending" ||
          stepStatus === "на рассмотрении" ||
          stepStatus === "" ||
          step.status === null ||
          step.status === undefined
        );
      });
    }

    // Если шагов нет, но статус не завершен, считаем активным
    return !approval.status || status === "";
  });

  // Проверяем, является ли текущий пользователь инициатором согласования
  const isInitiator =
    activeApproval && userData && activeApproval.initiator_id === userData.id;

  // Показываем статусный блок и кнопку отмены только если:
  // 1. Есть активное согласование
  // 2. Пользователь является инициатором (не согласующим)
  // 3. Не в режиме только для чтения (isReadOnly)
  const hasActiveApproval =
    activeApproval !== undefined && activeApproval !== null;
  const hasSteps = activeApproval?.steps && activeApproval.steps.length > 0;
  const showApprovalControls =
    hasActiveApproval &&
    (hasSteps ||
      activeApproval?.status === "На рассмотрении" ||
      activeApproval?.status === "на рассмотрении") &&
    isInitiator &&
    !isReadOnly;

  // Отладочная информация
  // console.log("Отладка согласования:", {
  //   approvalStatuses: approvalStatuses.map((a) => ({
  //     id: a.id,
  //     status: a.status,
  //     initiator_id: a.initiator_id,
  //     steps: a.steps?.map((s) => ({ id: s.id, status: s.status })),
  //   })),
  //   activeApproval: activeApproval
  //     ? {
  //         id: activeApproval.id,
  //         status: activeApproval.status,
  //         initiator_id: activeApproval.initiator_id,
  //       }
  //     : null,
  //   isInitiator,
  //   hasActiveApproval,
  //   showApprovalControls,
  //   isReadOnly,
  //   userData: userData?.id,
  // });

  // При активном согласовании сметы или КП блокируем редактирование (флаг с родительской страницы)
  const effectiveIsEditable = isEditable && !projectLockedByApproval;

  // Данные для правого блока (без клиентских вычислений — всё отдаёт бэкенд)
  const profitData = {
    total: headerData.contract_value || 0,
    // Доход = total_with_nds_from_cp (хардкод 100%)
    income: headerData.total_with_nds_from_cp || 0,
    incomePercent: 100, // Хардкод 100% для дохода
    // Себестоимость-план
    cost: headerData.cost_price || 0,
    costPercent:
      headerData.cost_price_percent !== undefined &&
      headerData.cost_price_percent !== null
        ? Number(headerData.cost_price_percent)
        : 0,
    // Косвенные расходы
    expense: headerData.indirect_expenditures || 0,
    expensePercent:
      headerData.indirect_expenditures_percent !== undefined &&
      headerData.indirect_expenditures_percent !== null
        ? Number(headerData.indirect_expenditures_percent)
        : 0,
    // Чистая прибыль
    netProfit: headerData.pure_income || 0,
    netProfitPercent:
      headerData.pure_income_percent !== undefined &&
      headerData.pure_income_percent !== null
        ? Number(headerData.pure_income_percent)
        : 0,
  };

  // Данные для блока сумм
  const sums = [
    {
      value: headerData.contract_value || 0,
      label: "Сумма основного договора",
      className: "main",
    },
    {
      value: headerData.contract_value_before_nds || 0,
      label: `Сумма за вычетом НДС ${headerData.nds_percent || 0}%`,
      className: "gray",
      ndsValue: headerData.nds_value || 0,
    },
    {
      value: headerData.contract_value_construction || 0,
      label: `На разработку ${
        headerData.contract_value_construction_percent || 0
      }%`,
      className: "blue",
    },
    {
      value: headerData.cost_price || 0, // Себестоимость (из API)
      label: "Себестоимость",
      className: "green",
    },
  ];

  const deviation = headerData.difference || 0;
  const deviationColor = deviation < 0 ? "red" : "green";
  const canEditDevelopmentPercent = effectiveIsEditable;

  const handleDevelopmentPercentChange = (e) => {
    const value = e.target.value.replace(",", ".");
    if (!/^\d*\.?\d*$/.test(value)) return;
    setDevelopmentPercentDraft(value);
  };

  const handleDevelopmentPercentFocus = (e) => {
    e.target.select();
    setIsDevelopmentPercentEditing(true);
  };

  const handleDevelopmentPercentCancel = () => {
    setDevelopmentPercentDraft(developmentPercentApplied);
    setIsDevelopmentPercentEditing(false);
  };

  const handleDevelopmentPercentApply = async () => {
    if (!projectId || !estimateData) return;

    const normalizedPercent = formatPercentDisplay(
      parsePercentValue(developmentPercentDraft)
    );
    const appliedPercent = parsePercentValue(developmentPercentApplied);
    const nextPercent = parsePercentValue(normalizedPercent);

    if (nextPercent === appliedPercent) {
      setIsDevelopmentPercentEditing(false);
      return;
    }

    setIsDevelopmentPercentSubmitting(true);
    try {
      await updateThresholdCostPercent(projectId, nextPercent);
      await loadEstimateData(projectId);

      setDevelopmentPercentApplied(normalizedPercent);
      setDevelopmentPercentDraft(normalizedPercent);
      setIsDevelopmentPercentEditing(false);
    } catch (err) {
      console.error("Ошибка обновления процента 'На разработку':", err);
      setErrorMessage(
        err?.message || "Не удалось пересчитать смету после изменения процента"
      );
      setShowErrorModal(true);
    } finally {
      setIsDevelopmentPercentSubmitting(false);
    }
  };

  // Создаем значение контекста
  const contextValue = {
    projectId,
    updateHeaderData,
    headerData,
    // Добавляем данные для всех вкладок
    bimCoordinationData: headerData.bim_coordination || {},
    qualityControlData: headerData.quality_control || {},
    projectDeliveryData: headerData.project_delivery || {},
    projectManagementData: headerData.project_management || {},
    // Пробросим суммарные проценты из WorkTab через headerData.total_construction_design
    percentTotal:
      headerData.total_construction_design?.expenditures_percent?.threshold,
    // Флаг редактируемости сметы
    isEditable,
  };

  // Компонент модала успеха
  const SuccessModal = useMemo(() => {
    const Modal = ({ open, onClose, message }) => {
      if (!open) return null;

      return (
        <>
          <div className="approval-modal-backdrop" onClick={onClose} />
          <div className="approval-modal">
            <div className="approval-modal-title">Успешно!</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#f0f8f4",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#226a3a",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>✅</span>
              <span>{message}</span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn green"
                onClick={onClose}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      );
    };

    return Modal;
  }, []); // Пустой массив зависимостей, так как компонент не зависит от внешних переменных

  // Компонент модала ошибки
  const ErrorModal = useMemo(() => {
    const Modal = ({ open, onClose, message }) => {
      if (!open) return null;

      return (
        <>
          <div className="approval-modal-backdrop" onClick={onClose} />
          <div className="approval-modal">
            <div className="approval-modal-title">Ошибка</div>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                background: "#fff8f1",
                borderRadius: "7px",
                padding: "10px 14px",
                color: "#b26a00",
                fontSize: "16px",
                marginBottom: "18px",
                gap: "10px",
              }}
            >
              <span style={{ fontSize: "22px", marginTop: "2px" }}>⚠️</span>
              <span>{message}</span>
            </div>
            <div className="approval-modal-actions">
              <button
                type="button"
                className="approval-btn outline"
                onClick={onClose}
              >
                Понятно
              </button>
            </div>
          </div>
        </>
      );
    };

    return Modal;
  }, []); // Пустой массив зависимостей

  if (loading) {
    return <Preloader />;
  }

  if (error) {
    return <div>Ошибка загрузки данных: {error}</div>;
  }

  // Если данные еще не загружены, показываем прелоадер
  if (!headerData || !estimateData) {
    return <Preloader />;
  }

  return (
    <EstimateContext.Provider value={contextValue}>
      <div className={`estimate-container ${saving ? "saving" : ""}`}>
        {/* Одна строка: слева — № заявки и дата, справа — Версия и История версий */}
        <div className="estimate-header-row">
          <div className="estimate-header-left">
            {showApprovalControls && (
              <>
                {activeApproval.steps && activeApproval.steps.length > 0 && (
                  <div className="estimate-approval-status-block">
                    {activeApproval.steps.map((step, index) => (
                      <div
                        key={step.id || index}
                        className="estimate-approval-step"
                      >
                        <span className="estimate-approver-name">
                          {step.approver_name || "Не указан"}:
                        </span>
                        {getStatusIcon(step.status)}
                        <span className="estimate-step-status-text">
                          {getStatusText(step.status)}
                        </span>
                        {step.comment && (
                          <span className="estimate-step-comment">
                            ({step.comment})
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <button
                  className="estimate-cancel-approval-btn"
                  onClick={handleCancelApproval}
                  disabled={isCancellingApproval || saving}
                >
                  {isCancellingApproval ? "Отзыв..." : "Отозвать отправку"}
                </button>
              </>
            )}
            <div className="estimate-inputs-block estimate-inputs-row">
              <div className="estimate-input-group">
                <label>Введите № заявки на договор</label>
                <input
                  value={contractNumber}
                  onChange={(e) => setContractNumber(e.target.value)}
                  placeholder="Введите номер заявки"
                  disabled={!effectiveIsEditable}
                />
              </div>
              <div className="estimate-input-group">
                <label>Введите дату заявки на договор</label>
                <DatePicker
                  selected={contractDate}
                  onChange={(date) => setContractDate(date)}
                  dateFormat="dd.MM.yyyy"
                  placeholderText="Выберите дату"
                  locale="ru"
                  calendarStartDay={1}
                  className="estimate-datepicker"
                  popperClassName="estimate-datepicker-popper"
                  disabled={!effectiveIsEditable}
                />
              </div>
            </div>
          </div>
          <div className="estimate-version-badge-container">
            {estimateData?.version !== undefined &&
              estimateData?.version !== null && (
                <span className="estimate-version-badge">
                  Версия {estimateData.version}
                </span>
              )}
            <button
              className="estimate-history-btn"
              onClick={() => setShowVersionDrawer(true)}
              disabled={saving}
              style={{
                opacity: saving ? 0.6 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              <span className="estimate-history-icon">⟳</span> История версий
            </button>
          </div>
        </div>
        {!isEditable && (
          <div
            style={{
              background: "#fff8f1",
              border: "1px solid #ffe0b2",
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              <span style={{ fontSize: "24px" }}>⚠️</span>
              <span style={{ color: "#b26a00", fontSize: "14px" }}>
                Смета находится на этапе согласования, поэтому редактирование
                заблокировано
              </span>
            </div>
            {((cpApprovalStatus &&
              (cpApprovalStatus.toLowerCase() === "approved" ||
                cpApprovalStatus.toLowerCase() === "согласован")) ||
              (peApprovalStatus &&
                (peApprovalStatus.toLowerCase() === "approved" ||
                  peApprovalStatus.toLowerCase() === "согласован"))) && (
              <button
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Вы уверены, что хотите создать копию? Будет создана новая версия ТЭП, Сметы и КП без согласований."
                    )
                  ) {
                    return;
                  }

                  try {
                    setIsCreatingCopy(true);
                    await restoreVersion(projectId, null);
                    // Перезагружаем страницу после создания копии
                    setTimeout(() => {
                      window.location.reload();
                    }, 500);
                  } catch (error) {
                    console.error("Ошибка при создании копии:", error);
                    alert(`Ошибка при создании копии: ${error.message}`);
                  } finally {
                    setIsCreatingCopy(false);
                  }
                }}
                disabled={isCreatingCopy}
                style={{
                  padding: "8px 16px",
                  background: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: isCreatingCopy ? "not-allowed" : "pointer",
                  opacity: isCreatingCopy ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {isCreatingCopy ? "Создание копии..." : "Сделать копию"}
              </button>
            )}
          </div>
        )}
        <div className="estimate-dashboard">
          {/* 1. План рентабельности проекта — первый, на всю ширину */}
          <div className="estimate-profit-block estimate-profit-block-full">
            <div className="estimate-profit-title">
              План рентабельности проекта
            </div>
            <div
              style={{
                marginTop: "6px",
                marginBottom: "12px",
                fontSize: "12px",
                color: "#6c757d",
              }}
            >
              Примечание: итоговые значения в шапке обновляются только после
              сохранения. Показанные после калькуляции данные — предварительные.
            </div>
            <div className="estimate-profit-grid">
              <div className="estimate-profit-card">
                <div className="estimate-profit-icon">
                  <img src={Coinssvg} alt="Доход" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">Доход</div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.income.toLocaleString()}
                  </div>
                  <div className="estimate-profit-percent">
                    {profitData.incomePercent.toFixed(2)}%
                  </div>
                </div>
              </div>

              <div className="estimate-profit-card">
                <div className="estimate-profit-icon">
                  <img src={Calc} alt="Себестоимость" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">
                    Себестоимость - План
                  </div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.cost.toLocaleString()}
                  </div>
                  <div className="estimate-profit-percent">
                    {headerData.cost_price_percent !== undefined &&
                    headerData.cost_price_percent !== null
                      ? Number(headerData.cost_price_percent).toFixed(2)
                      : profitData.costPercent.toFixed(2)}
                    %
                  </div>
                </div>
              </div>

              <div className="estimate-profit-card">
                <div className="estimate-profit-icon">
                  <img src={House} alt="Косвенные расходы" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">Косвенные расходы</div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.expense.toLocaleString()}
                  </div>
                  <div className="estimate-profit-percent">
                    {profitData.expensePercent.toFixed(2)}%
                  </div>
                </div>
              </div>

              <div className="estimate-profit-card">
                <div className="estimate-profit-icon">
                  <img src={Sigma} alt="Чистая прибыль" />
                </div>
                <div className="estimate-profit-content">
                  <div className="estimate-profit-label">Чистая прибыль</div>
                  <div className="estimate-profit-value">
                    <span className="currency">₸</span>{" "}
                    {profitData.netProfit.toLocaleString()}
                  </div>
                  <div className="estimate-profit-percent">
                    {profitData.netProfitPercent.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Сумма основного договора и Сумма за вычетом НДС — в один ряд */}
          <div className="estimate-sums-row">
            <div className="estimate-sum-card main">
              <div className="estimate-sum-label">
                Сумма основного договора
              </div>
              <div className="estimate-sum-value">
                <span className="currency">₸</span>{" "}
                {sums[0].value.toLocaleString()}
              </div>
            </div>
            {headerData.nds_percent > 0 && (
              <div className="estimate-sum-card gray">
                <div className="estimate-sum-label">
                  Сумма за вычетом НДС {headerData.nds_percent}%
                </div>
                <div className="estimate-sum-value">
                  <span className="currency">₸</span>{" "}
                  {sums[1].value.toLocaleString()}
                </div>
                <div className="estimate-sum-nds">
                  Сумма НДС <span className="currency">₸</span>{" "}
                  {sums[1].ndsValue.toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {/* 3. На разработку, Себестоимость, Отклонения */}
          <div className="estimate-list-block">
            <div className="estimate-list-items">
              <div className="estimate-list-item blue">
                <div className="estimate-list-dot blue"></div>
                <div className="estimate-list-content">
                  <div className="estimate-list-label">На разработку</div>
                  <div className="estimate-list-value">
                    {canEditDevelopmentPercent ? (
                      <span className="estimate-development-percent-control">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="estimate-development-percent-input"
                          value={developmentPercentDraft}
                          onChange={handleDevelopmentPercentChange}
                          onFocus={handleDevelopmentPercentFocus}
                          disabled={isDevelopmentPercentSubmitting || saving}
                          aria-label="Процент на разработку"
                        />
                        <span className="estimate-development-percent-unit">%</span>
                        {isDevelopmentPercentEditing && (
                          <span
                            className="estimate-development-percent-actions"
                            role="group"
                          >
                            <button
                              type="button"
                              className="estimate-development-percent-action estimate-development-percent-action-apply"
                              onClick={handleDevelopmentPercentApply}
                              disabled={isDevelopmentPercentSubmitting || saving}
                              title="Применить"
                              aria-label="Применить"
                            >
                              ✓
                            </button>
                            <button
                              type="button"
                              className="estimate-development-percent-action estimate-development-percent-action-cancel"
                              onClick={handleDevelopmentPercentCancel}
                              disabled={isDevelopmentPercentSubmitting || saving}
                              title="Отменить"
                              aria-label="Отменить"
                            >
                              ✕
                            </button>
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="estimate-development-percent-readonly">
                        {formatPercentDisplay(
                          Number(headerData.contract_value_construction_percent)
                        )}
                        %
                      </span>
                    )}
                    <span className="currency">₸</span>{" "}
                    {sums[2].value.toLocaleString()}
                  </div>
                </div>
              </div>
              <div className="estimate-list-item green">
                <div className="estimate-list-dot green"></div>
                <div className="estimate-list-content">
                  <div className="estimate-list-label">Себестоимость</div>
                  <div className="estimate-list-value">
                    <span className="currency">₸</span>{" "}
                    {sums[3].value.toLocaleString()}
                  </div>
                </div>
              </div>
              <div className={`estimate-list-item ${deviationColor}`}>
                <div className={`estimate-list-dot ${deviationColor}`}></div>
                <div className="estimate-list-content">
                  <div className="estimate-list-label">Отклонения</div>
                  <div className="estimate-list-value">
                    <span className="currency">₸</span>{" "}
                    {deviation.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="estimate-table-block">
          <div className="estimate-bottom-tabs">
            {bottomTabs.map((tab) => (
              <button
                key={tab.id}
                className={`estimate-bottom-tab-btn${
                  activeBottomTab === tab.id ? " active" : ""
                }`}
                onClick={() => {
                  if (!saving) {
                    setActiveBottomTab(tab.id);
                    // Сохраняем активную нижнюю вкладку в localStorage
                    localStorage.setItem(
                      `estimate_activeBottomTab_${projectId}`,
                      tab.id
                    );
                  }
                }}
                disabled={saving}
                style={{
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* WorkTab всегда загружен для основной логики сохранения */}
          <div
            style={{ display: activeBottomTab === "work" ? "block" : "none" }}
          >
            <WorkTab
              ref={workTabRef}
              projectId={projectId}
              isSaving={saving}
              isEditable={effectiveIsEditable}
            />
          </div>

          {/* Остальные вкладки загружаются условно */}
          {activeBottomTab === "project" && (
            <ProjectTab ref={projectTabRef} isEditable={effectiveIsEditable} />
          )}
          {activeBottomTab === "lead" && (
            <LeadTab ref={leadTabRef} isEditable={effectiveIsEditable} />
          )}
          {activeBottomTab === "quality" && (
            <QualityTab ref={qualityTabRef} isEditable={effectiveIsEditable} />
          )}
          {activeBottomTab === "bim" && (
            <BimTab ref={bimTabRef} isEditable={effectiveIsEditable} />
          )}
          {activeBottomTab === "contract" && (
            <ContractSectionsTab
              ref={contractSectionsTabRef}
              projectId={projectId}
              isSaving={saving}
              isEditable={effectiveIsEditable}
            />
          )}
          <div className="estimate-actions">
            <div
              style={{
                fontSize: "12px",
                color: "#666",
                marginBottom: "8px",
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              💡 Кнопка "Сохранить" сохраняет данные со всех вкладок
              одновременно
            </div>
            <button
              className="btn-send-approval"
              onClick={() => setShowApprovalModal(true)}
              disabled={saving || isSubmittingApproval || !effectiveIsEditable}
              style={{
                opacity:
                  saving || isSubmittingApproval || !isEditable ? 0.6 : 1,
                cursor:
                  saving || isSubmittingApproval || !isEditable
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {isSubmittingApproval
                ? "Отправка..."
                : "Отправить на согласование"}
            </button>
            <div style={{ position: "relative", display: "inline-block" }}>
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={
                  saving ||
                  !isEditable ||
                  (activeBottomTab !== "contract" &&
                    ((headerData?.percentTotalFromTable ??
                      headerData?.total_construction_design?.expenditures_percent
                        ?.threshold) > 101 ||
                      (workTabRef.current &&
                        workTabRef.current.hasValidationErrors &&
                        workTabRef.current.hasValidationErrors())))
                }
                style={{
                  opacity:
                    saving ||
                    !isEditable ||
                    (activeBottomTab !== "contract" &&
                      ((headerData?.percentTotalFromTable ??
                        headerData?.total_construction_design
                          ?.expenditures_percent?.threshold) > 101 ||
                        (workTabRef.current &&
                          workTabRef.current.hasValidationErrors &&
                          workTabRef.current.hasValidationErrors())))
                      ? 0.6
                      : 1,
                  cursor:
                    saving ||
                    !isEditable ||
                    (activeBottomTab !== "contract" &&
                      ((headerData?.percentTotalFromTable ??
                        headerData?.total_construction_design
                          ?.expenditures_percent?.threshold) > 101 ||
                        (workTabRef.current &&
                          workTabRef.current.hasValidationErrors &&
                          workTabRef.current.hasValidationErrors())))
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {saving ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid #ffffff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                    Сохранение...
                  </div>
                ) : (
                  "Сохранить"
                )}
              </button>
              {activeBottomTab !== "contract" &&
                ((headerData?.percentTotalFromTable ??
                  headerData?.total_construction_design?.expenditures_percent
                    ?.threshold) > 101 ||
                  (workTabRef.current &&
                    workTabRef.current.hasValidationErrors &&
                    workTabRef.current.hasValidationErrors())) && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      right: 0,
                      transform: "translateY(-8px)",
                      background: "#fff8f1",
                      color: "#b26a00",
                      border: "1px solid #ffe0b2",
                      borderRadius: "6px",
                      padding: "8px 10px",
                      fontSize: "12px",
                      whiteSpace: "normal",
                      lineHeight: 1.35,
                      wordBreak: "break-word",
                      maxWidth: "min(360px, calc(100vw - 32px))",
                      zIndex: 10,
                      pointerEvents: "none",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                    }}
                  >
                    {(headerData?.percentTotalFromTable ??
                      headerData?.total_construction_design?.expenditures_percent
                        ?.threshold) > 101
                      ? "Сумма долей превышает 101%. Сохранение недоступно."
                      : "% доля и средняя ставка по всем разделам должны быть > 0."}
                  </div>
                )}
            </div>
            {/* <button
              className="btn-print-pdf"
              disabled={saving}
              style={{
                opacity: saving ? 0.6 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M14 2H2V14H14V2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 6H10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 9H10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6 12H8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Печать PDF
            </button> */}
          </div>
        </div>
        <VersionHistoryDrawer
          open={showVersionDrawer}
          onClose={() => setShowVersionDrawer(false)}
          projectId={projectId}
          onVersionRestored={() => loadEstimateData(projectId)}
        />
        <ApprovalModal
          open={showApprovalModal}
          onClose={() => setShowApprovalModal(false)}
          onSubmit={handleApprovalSubmit}
          projectData={projectData}
          isSubmitting={isSubmittingApproval}
        />
        <SuccessModal
          open={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          message={successMessage}
        />
        <ErrorModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          message={errorMessage}
        />
      </div>
    </EstimateContext.Provider>
  );
};

const Estimate = ({ projectId, onSave, isReadOnly = false }) => {
  return (
    <EstimateStateProvider>
      <EstimateComponent
        projectId={projectId}
        onSave={onSave}
        isReadOnly={isReadOnly}
      />
    </EstimateStateProvider>
  );
};

export default Estimate;
